import { ApiClient, type ApiRequestInit } from '@schemas'

export const DEV_ACTOR_ID_STORAGE_KEY = 'honeyshow/devActorId'
export const DEV_ROLE_STORAGE_KEY = 'honeyshow/devRole'

export const baseUrl = import.meta.env.VITE_API_BASE_URL ?? ''

export function handleUnauthorizedRedirect(pathname: string, search: string) {
  const redirect = encodeURIComponent(pathname + search)
  if (!pathname.startsWith('/login')) {
    window.location.assign(`/login?redirect=${redirect}`)
  }
}

const apiWriteListeners = new Set<() => void>()
const personAccessDenialListeners = new Set<(lookup: boolean) => void>()
export function subscribeToPersonAccessDenials(listener: (lookup: boolean) => void) {
  personAccessDenialListeners.add(listener)
  return () => {
    personAccessDenialListeners.delete(listener)
  }
}
/** `lookup` marks a refused matching search, which reports its own error where it was typed. */
export function clearPersonAccess(lookup = false) {
  for (const listener of personAccessDenialListeners) listener(lookup)
}
// A 401 ends the session everywhere. A 403 says something about People access only when the
// refused request was itself a show's people, entrant or staff request.
const PERSON_ACCESS_PATH = /\/shows\/[^/]+\/(?:people|entrants|staff)(?:[/?]|$)/
const PERSON_MATCH_PATH = /\/shows\/[^/]+\/(?:staff\/)?people\/matches(?:\?|$)/
function personAccessDenial(input: RequestInfo | URL, status: number) {
  if (status === 401) return { lookup: false }
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
  if (status !== 403 || !PERSON_ACCESS_PATH.test(url)) return null
  return { lookup: PERSON_MATCH_PATH.test(url) }
}
export function subscribeToApiWrites(listener: () => void) {
  apiWriteListeners.add(listener)
  return () => {
    apiWriteListeners.delete(listener)
  }
}

export const fetchWithActorHeaders: typeof fetch = async (input, init) => {
  const headers = new Headers(init?.headers ?? undefined)

  headers.delete('x-actor-id')
  headers.delete('x-actor-role')

  const response = await fetch(input, { ...init, headers, credentials: 'include' })
  const denial = personAccessDenial(input, response.status)
  if (denial) clearPersonAccess(denial.lookup)
  try {
    if (typeof window !== 'undefined' && response.status === 401) {
      handleUnauthorizedRedirect(window.location.pathname, window.location.search)
    }
  } catch {
    // ignore navigation errors in test environments
  }
  if (
    response.ok &&
    !(init as ApiRequestInit | undefined)?.readOnly &&
    !['GET', 'HEAD', 'OPTIONS'].includes((init?.method ?? 'GET').toUpperCase())
  ) {
    for (const listener of apiWriteListeners) listener()
  }
  return response
}

export const apiClient = new ApiClient({ baseUrl, fetchImpl: fetchWithActorHeaders })
export type { ApiClientConfig } from '@schemas'
